import { Section } from '@/components/console/Section';
import { when } from '@/lib/format';
import type { FullBodyRowView } from '@/lib/people-data';
import s from '@/pages/console/console.module.css';

/**
 * The full-body assessments (pv-auth GET /provider/admin/people/:id
 * `fullBody`; FULL_BODY_ASSESSMENT_2026-10-18): one block per run, newest
 * first, every measure as the client read it, with its words, its number
 * and its source, and "not measured" with the reason where it was not. A
 * null is not measured, never zero. Absent from the detail until pv-auth
 * #442 is deployed, and the section says so.
 */
const VIEW_LABEL: Record<string, string> = { front: 'Facing the camera', sideRight: 'Right side to the camera', sideLeft: 'Left side to the camera' };
const label = (key: string) => key.replace(/_/g, ' ');

export function FullBodySection({ rows }: { rows: FullBodyRowView[] | undefined }) {
  const count = rows ? rows.length : null;
  const summary = rows === undefined ? 'not in this pv-auth yet (deploy #442)' : rows.length === 0 ? 'none yet; the offer comes on the second check day' : `standing, head to toe: the stills as estimates from the camera, the movements from the engine; newest first`;
  return (
    <Section title="Full-body assessment" summary={summary} count={count} defaultOpen={!!rows && rows.length > 0} id="full-body">
      {rows === undefined && <div className={s.empty}>This pv-auth does not send fullBody yet.</div>}
      {rows && rows.length === 0 && <div className={s.empty}>No standing assessment yet.</div>}
      {rows?.map((r) => (
        <div key={r.id} className={s.jNote} data-testid={`full-body-${r.id}`}>
          <div>
            <strong>{r.localDate}</strong> · {r.status === 'stopped' ? `stopped at ${label(r.stoppedAt ?? 'unknown')} (${label(r.stoppedReason ?? 'unknown')})` : 'complete'} · {r.summary.measured} measured, {r.summary.notMeasured} not measured · movements {r.summary.movementsMeasured} measured, {r.summary.movementsNotMeasured} not · turns to the {r.turnConvention}
          </div>
          <div className={s.sub}>{when(r.completedAt)}</div>
          {(['front', 'sideRight', 'sideLeft'] as const).map((k) => {
            const v = r.views[k];
            return (
              <div key={k} style={{ marginTop: '0.5rem' }}>
                <div className={s.sub}>{VIEW_LABEL[k]}{v ? ` · ${v.framing === 'whole_body' ? 'whole body in frame' : 'partly in frame'}` : ' · not taken'}</div>
                {v && (
                  <dl className={s.facts}>
                    {v.measures.map((m) => (
                      <div key={m.key} style={{ display: 'contents' }}>
                        <dt>{label(m.key)}</dt>
                        <dd>
                          {m.measured ? (
                            <>
                              {m.words} ({m.value}{m.unit === 'deg' ? ' deg' : m.unit === 'ratio' ? ' of height' : ''}; {m.source === 'client_estimate' ? 'client estimate' : 'engine'}, {m.confidence} confidence)
                            </>
                          ) : (
                            <span className={s.missing}>not measured{m.notMeasuredReason ? ` (${label(m.notMeasuredReason)})` : ''}</span>
                          )}
                        </dd>
                      </div>
                    ))}
                  </dl>
                )}
              </div>
            );
          })}
          <div style={{ marginTop: '0.5rem' }}>
            <div className={s.sub}>Movement</div>
            <dl className={s.facts}>
              {r.movements.map((m) => (
                <div key={m.id} style={{ display: 'contents' }}>
                  <dt>{label(m.id)}</dt>
                  <dd>{m.measured ? `${m.achievedDeg} deg${m.readFrom ? ` (read from the ${m.readFrom})` : ''}` : <span className={s.missing}>not measured{m.notMeasuredReason ? ` (${label(m.notMeasuredReason)})` : ''}</span>}</dd>
                </div>
              ))}
            </dl>
          </div>
          <div style={{ marginTop: '0.5rem' }}>
            <div className={s.sub}>How it felt</div>
            <div>{r.felt ? `${label(r.felt.feel)}${r.felt.where ? `, ${label(r.felt.where)}` : ''} (${r.felt.source}, ${when(r.felt.at)})` : <span className={s.missing}>not asked</span>}</div>
          </div>
        </div>
      ))}
    </Section>
  );
}
